/**
 * A pesquisa global do painel (⌘K / Ctrl+K), no lugar do Spotlight do Mantine: todas as
 * páginas do menu, mais as acções que a app acrescentar («Pesquisar alunos…»). Procura sem
 * ligar a acentos e todas as palavras têm de aparecer no nome, no caminho ou na descrição.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { router } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import { Command } from "cmdk";
import { CornerDownLeft, Search } from "lucide-react";
import { EntradaPaleta, semAcentos } from "./menu";
import { useSaida } from "../sobreposicoes";
import { cn } from "@/lib/utils";

export interface AccaoPaleta {
    chave: string;
    rotulo: string;
    descricao?: string;
    icone?: string;
    /** Palavras extra para a pesquisa. */
    palavras?: string[];
    onSelect: () => void;
}

function filtro(valor: string, pesquisa: string, palavras?: string[]): number {
    const termos = semAcentos(pesquisa).split(/\s+/).filter(Boolean);
    if (termos.length === 0) {
        return 1;
    }
    const texto = semAcentos(`${valor} ${(palavras ?? []).join(" ")}`);
    return termos.every((t) => texto.includes(t)) ? 1 : 0;
}

export function PaletaComandos({ aberta, onFechar, entradas, accoes = [] }: {
    aberta: boolean;
    onFechar: () => void;
    entradas: EntradaPaleta[];
    accoes?: AccaoPaleta[];
}) {
    const { montada, aSair } = useSaida(aberta);
    const [termo, setTermo] = useState("");
    const onFecharRef = useRef(onFechar);
    onFecharRef.current = onFechar;

    useEffect(() => {
        if (!aberta) {
            return;
        }
        setTermo("");
        const html = document.documentElement;
        const antes = html.style.overflow;
        html.style.overflow = "hidden";
        const tecla = (e: KeyboardEvent) => e.key === "Escape" && onFecharRef.current();
        document.addEventListener("keydown", tecla);
        return () => {
            html.style.overflow = antes;
            document.removeEventListener("keydown", tecla);
        };
    }, [aberta]);

    const visitar = (e: EntradaPaleta) => {
        onFechar();
        if (e.externo) {
            window.open(e.href, "_blank", "noopener");
        } else {
            router.visit(e.href);
        }
    };

    // Sem pesquisa mostram-se só as primeiras páginas (o menu inteiro seria comprido demais).
    const mostradas = useMemo(() => (termo.trim() ? entradas : entradas.slice(0, 12)), [termo, entradas]);

    if (!montada || typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <div className={cn("fixed inset-0 z-[250] flex items-start justify-center px-3 pt-[12vh] sm:px-4", aSair && "pointer-events-none")} aria-hidden={aSair || undefined}>
            <div className={cn("absolute inset-0 bg-zinc-950/45 backdrop-blur-[2px]", aSair ? "animate-out fade-out-0 fill-mode-forwards duration-150" : "animate-in fade-in-0")} onMouseDown={onFechar} aria-hidden />
            <Command
                label="Pesquisar no painel"
                filter={filtro}
                loop
                className={cn("relative w-full max-w-xl overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-900", aSair ? "animate-out fade-out-0 zoom-out-95 fill-mode-forwards duration-150" : "animate-in fade-in-0 zoom-in-95")}
            >
                <div className="flex items-center gap-3 border-b border-zinc-100 px-4 dark:border-zinc-800">
                    <Search className="size-5 shrink-0 text-zinc-400" aria-hidden />
                    <Command.Input
                        autoFocus
                        value={termo}
                        onValueChange={setTermo}
                        placeholder="Ir para uma página ou fazer uma acção…"
                        className="h-14 w-full bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-zinc-100"
                    />
                    <kbd className="hidden shrink-0 rounded border border-zinc-200 px-1.5 py-0.5 text-[11px] font-medium text-zinc-400 sm:block dark:border-zinc-700">Esc</kbd>
                </div>
                {/* Sem `label`, o cmdk dá à lista o nome «Suggestions», em inglês, para os leitores de ecrã. */}
                <Command.List label="Resultados" className="scroll-bonito max-h-[min(60vh,440px)] overflow-y-auto overscroll-contain p-2">
                    <Command.Empty className="px-4 py-10 text-center text-sm text-zinc-500">Nada encontrado. Tenta outras palavras.</Command.Empty>
                    {accoes.length > 0 && (
                        <Command.Group heading="Acções" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-zinc-400">
                            {accoes.map((a) => (
                                <Command.Item
                                    key={a.chave}
                                    value={`${a.rotulo} ${a.descricao ?? ""}`}
                                    keywords={a.palavras}
                                    onSelect={() => {
                                        onFechar();
                                        a.onSelect();
                                    }}
                                    className={CLASSE_ITEM}
                                >
                                    <Icon icon={a.icone || "mdi:lightning-bolt-outline"} className="size-5 shrink-0 text-zinc-400" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate font-medium">{a.rotulo}</span>
                                        {a.descricao && <span className="block truncate text-xs text-zinc-500">{a.descricao}</span>}
                                    </span>
                                    <CornerDownLeft className="hidden size-4 shrink-0 text-zinc-400 group-data-[selected=true]:block" aria-hidden />
                                </Command.Item>
                            ))}
                        </Command.Group>
                    )}
                    <Command.Group heading="Páginas" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-zinc-400">
                        {mostradas.map((e) => (
                            <Command.Item key={e.chave} value={`${e.rotulo} ${e.caminho} ${e.chave} ${e.descricao ?? ""}`} onSelect={() => visitar(e)} className={CLASSE_ITEM}>
                                <Icon icon={e.icone || "mdi:file-outline"} className="size-5 shrink-0 text-zinc-400 group-data-[selected=true]:text-portal dark:group-data-[selected=true]:text-portal-claro" />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate font-medium">{e.rotulo}</span>
                                    <span className="block truncate text-xs text-zinc-500">{e.caminho || "Menu principal"}</span>
                                </span>
                                <CornerDownLeft className="hidden size-4 shrink-0 text-zinc-400 group-data-[selected=true]:block" aria-hidden />
                            </Command.Item>
                        ))}
                    </Command.Group>
                </Command.List>
                <div className="hidden items-center gap-4 border-t border-zinc-100 px-4 py-2 text-[11px] text-zinc-400 sm:flex dark:border-zinc-800">
                    <span><kbd className="font-sans">↑↓</kbd> navegar</span>
                    <span><kbd className="font-sans">↵</kbd> abrir</span>
                    <span><kbd className="font-sans">Esc</kbd> fechar</span>
                </div>
            </Command>
        </div>,
        document.body,
    );
}

const CLASSE_ITEM =
    "group flex min-h-12 cursor-pointer select-none items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-zinc-700 outline-none data-[selected=true]:bg-zinc-100 data-[selected=true]:text-zinc-900 dark:text-zinc-300 dark:data-[selected=true]:bg-zinc-800 dark:data-[selected=true]:text-white";
